import React, { useState } from 'react'
import classNames from 'classnames'
import { flowRight } from 'lodash'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { withErrorBoundaryNoRender } from 'lib/errorBoundary'
import localStorage from 'lib/localStorage'
import { logException } from 'lib/errors'

import Button from 'components/UI/Button'
import AppContext from 'components/AppContext'
import Link from 'components/Link'

import './service-notification.pcss'

/**
 * id: unique ID. Increment this as if user has hidden specific ID, it will no longer show.
 * message: string
 * severity: higher severity messages will be displayed first and can have different styles(set 0 for low severity, 1 for medium)
 * dateStart: "YYYY-DD-HH hh:mm:ss" (optional)
 * dateEnd: "YYYY-DD-HH hh:mm:ss" (optional)
 */

const useServiceNotification = (closedNotifications) => {
    const { t } = useTranslation(['serviceNotification'])
    const nowUTC = moment(Date.now()).utc()

    const notifications = t('notifications', { returnObjects: true })
        .filter((n) => !closedNotifications.includes(n.id))
        .filter((n) => (!n.dateStart || moment(n.dateStart).isBefore(nowUTC)) && (!n.dateEnd || moment(n.dateEnd).isAfter(nowUTC)))
        .sort((a, b) => a.severity < b.severity ? -1 : a.severity > b.severity ? 1 : 0)
        .reverse()

    return notifications.length ? notifications.shift() : null
}

const LOCAL_STORAGE_KEY_PREFIX = 'myvehicles-servicenotification-'

const getSavedClosedNotifications = () => {
    try {
        const val = localStorage.getItem(`${LOCAL_STORAGE_KEY_PREFIX}-closed`)
        return val ? JSON.parse(val) : []
    } catch (e) {
        logException(e)
        return []
    }
}

const saveClosedNotification = (id) => {
    const items = getSavedClosedNotifications()
    items.push(id)
    localStorage.setItem(`${LOCAL_STORAGE_KEY_PREFIX}-closed`, JSON.stringify(items))
}

const ServiceNotification = () => {
    const { t } = useTranslation(['common'])
    const [closedNotifications, setClosedNotifications] = useState(getSavedClosedNotifications())
    const notification = useServiceNotification(closedNotifications)

    const closeNotification = () => {
        setClosedNotifications([...closedNotifications, notification.id])
        saveClosedNotification(notification.id)
    }

    return notification ? (
        <AppContext context="serviceNotification" key={notification.id}>
            <div className={classNames('service-notification', `service-notification--severity-${notification.severity}`)}>
                <div className="service-notification__inner">
                    <p className="animated fadeIn fastest">
                        {notification.message}
                        {notification.link && notification.linkText && (
                            <Link
                                to={notification.link}
                                trackName="serviceNotificationLink"
                            >
                                &nbsp;{notification.linkText}
                            </Link>
                        )}
                    </p>
                    <div className="service-notification__actions">
                        <Button
                            onClick={closeNotification}
                            className="button button--plain"
                            trackName="close"
                            trackLabel={notification.id}
                        >
                            {t('common:close')}
                        </Button>
                    </div>
                </div>
            </div>
        </AppContext>
    ) : null
}

export default flowRight(
    withErrorBoundaryNoRender,
)(ServiceNotification)
